import moment from 'moment'
import { Expose, Transform, Type } from 'class-transformer'
import { AxiosError } from 'axios'
import { Truck } from 'react-feather'

import { skuit } from 'src/services/skuit'
import { ActionDirection, WorkflowSequence } from 'src/modules/supply_chain/components/Workflow'
import { handleErrorMessage } from 'src/components/Notification'

import { Address } from './Address'
import { Appointment } from './Appointment'
import { Base } from './Base'
import { Carrier } from './Carrier'
import { Client } from './Client'
import { Facility } from './Facility'
import { Order } from './Order'

enum ASNStatus {
  pending = 'Pending',
  arrived = 'Arrived',
  receiving = 'Receiving',
  received = 'Received',
  canceled = 'Canceled',
}

export enum ASNStatusEnum {
  pending = 'pending',
  arrived = 'arrived',
  receiving = 'receiving',
  received = 'received',
  canceled = 'canceled',
}

export class ASN extends Base {
  static Status = ASNStatus
  static type = 'asn'
  static typeDisplay = 'Advanced Shipping Notice'
  static icon = Truck

  type = ASN.type
  typeDisplay = ASN.typeDisplay
  icon = ASN.icon

  @Expose({ name: 'xid' })
  guid: string = ''

  name: string = ''

  @Expose({ name: 'transload_number' })
  transloadNumber?: string

  @Expose({ name: 'tracking_number' })
  trackingNumber?: string

  @Expose({ name: 'bill_of_lading_number' })
  bol?: string

  @Expose({ name: 'expected_pallet_count' })
  expectedPalletCount?: string

  @Type(() => Client)
  client?: Client

  @Type(() => Facility)
  facility?: Facility

  @Type(() => Order)
  order?: Order

  consignee?: string

  @Type(() => Address)
  address?: Address

  @Type(() => Carrier)
  carrier?: Carrier

  @Type(() => Appointment)
  appointment?: Appointment

  status?: string

  @Expose({ name: 'internal_notes' })
  internalNotes: string = ''

  details: string = ''

  @Expose({ name: 'arrived_on' })
  @Transform((value) => value ? moment(value) : null)
  arrivedOn?: moment.Moment

  @Expose({ name: 'receiving_on' })
  @Transform((value) => value ? moment(value) : null)
  receivingOn?: moment.Moment

  @Expose({ name: 'received_on' })
  @Transform((value) => value ? moment(value) : null)
  receivedOn?: moment.Moment

  @Expose({ name: 'canceled_on' })
  @Transform((value) => value ? moment(value) : null)
  canceledOn?: moment.Moment

  @Expose({ name: 'estimated_arrival_date' })
  @Transform((value) => value ? moment(value) : null)
  estimatedArrivalDate?: moment.Moment

  get workflowStatus() {
    if (this.status) {
      return this.status.charAt(0).toUpperCase() + this.status.slice(1)
    }
    return ''
  }

  get workflow(): WorkflowSequence {
    if (this.status === ASNStatusEnum.canceled) {
      return [
        [ASNStatus.pending, this.createdOn],
        [ASNStatus.arrived, this.arrivedOn],
        [ASNStatus.receiving, this.receivingOn],
        [ASNStatus.canceled, this.canceledOn],
      ]
    }
    return [
      [ASNStatus.pending, this.createdOn],
      [ASNStatus.arrived, this.arrivedOn],
      [ASNStatus.receiving, this.receivingOn],
      [ASNStatus.received, this.receivedOn],
    ]
  }

  getWorkflowActions = (onAction?: () => void): { [status: string]: ActionDirection } => {
    return {
      [ASNStatus.pending]: {
        forward: [
          {
            label: 'ASN Arrived',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.arrived })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.canceled })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
      },
      [ASNStatus.arrived]: {
        forward: [
          {
            label: 'Start Receiving',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.receiving })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'ASN Not Arrived',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.pending })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.canceled })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
      },
      [ASNStatus.receiving]: {
        forward: [
          {
            label: 'Done Receiving',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.received })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'Reset',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.arrived })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
        cancel: [
          {
            label: 'Cancel',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.canceled })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
      },
      [ASNStatus.received]: {
        backward: [
          {
            label: 'Continue Receiving',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.arrived })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
      },
      [ASNStatus.canceled]: {
        backward: [
          {
            label: 'Uncancel',
            onClick: ({ complete }) => {
              skuit.sc.asn
                .patch(this.guid, { status: ASNStatusEnum.pending })
                .then(() => onAction?.())
                .catch((error: AxiosError) => handleErrorMessage(error, 'updat', 'ASN'))
                .finally(() => complete())
            },
          },
        ],
      },
    }
  }
}
